import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Flame, Award, Lock, GraduationCap } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Shows a signed-in reader's learning streak + milestone badges.
 * Renders nothing until stats load; on Home we hide it when there's no activity yet.
 */
export default function ReadingStats({ hideWhenEmpty = false, className = "" }) {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!user) { setStats(null); return; }
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [user]);

  if (!user || !stats) return null;
  const hasActivity = stats.total_completed > 0 || stats.current_streak > 0;
  if (hideWhenEmpty && !hasActivity) return null;

  const streak = stats.current_streak;

  return (
    <div className={`border border-line bg-ink p-8 lg:p-10 ${className}`} data-testid="reading-stats">
      <p className="overline text-crimson mb-8">Your progress</p>

      <div className="grid sm:grid-cols-3 gap-px bg-line border border-line mb-10">
        {/* Streak */}
        <div className="bg-ink p-6 flex items-center gap-4" data-testid="stat-streak">
          <Flame className={`w-8 h-8 ${streak > 0 ? "text-crimson" : "text-vellum-faint"}`} />
          <div>
            <p className="font-serif text-4xl font-light text-vellum leading-none">{streak}</p>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-vellum-muted mt-2">
              {streak === 1 ? "day streak" : "days streak"}
            </p>
          </div>
        </div>
        {/* Total steps */}
        <div className="bg-ink p-6 flex items-center gap-4" data-testid="stat-steps">
          <Award className="w-8 h-8 text-brass" />
          <div>
            <p className="font-serif text-4xl font-light text-vellum leading-none">{stats.total_completed}</p>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-vellum-muted mt-2">steps completed</p>
          </div>
        </div>
        {/* Curriculums finished */}
        <div className="bg-ink p-6 flex items-center gap-4" data-testid="stat-pathways">
          <GraduationCap className="w-8 h-8 text-brass" />
          <div>
            <p className="font-serif text-4xl font-light text-vellum leading-none">{stats.curriculums_completed}</p>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-vellum-muted mt-2">pathways finished</p>
          </div>
        </div>
      </div>

      {/* Badges */}
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-vellum-faint mb-5">Badges</p>
      <div className="flex flex-wrap gap-3" data-testid="badge-row">
        {stats.badges.map((b, i) => (
          <motion.div
            key={b.key}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            data-testid={`badge-${b.key}`}
            title={b.threshold ? `${b.threshold} steps` : "Finish a pathway"}
            className={`inline-flex items-center gap-2 px-4 py-2.5 border font-mono text-xs uppercase tracking-[0.15em] transition-colors ${
              b.earned
                ? "border-crimson text-vellum bg-crimson/10"
                : "border-line text-vellum-faint"
            }`}
          >
            {b.earned ? <Award className="w-3.5 h-3.5 text-crimson" /> : <Lock className="w-3.5 h-3.5" />}
            {b.label}
          </motion.div>
        ))}
      </div>
    </div>
  );
}
